import RemoteDataTable from "components/shared-components/RemoteTable/data-table";
import CustomDateFilter from "components/shared-components/RemoteTable/custom-date-filter";
import CustomSelectFilter from "components/shared-components/RemoteTable/custom-select-filter";
import {
  CUSTOM_DATE_FILTER_TABLE_TYPE,
  CUSTOM_SELECT_FILTER_TABLE_TYPE,
} from "constants/RemoteDataTableContanst";
import {
  useCustomerAuditLogs,
  useLogAdminUsers,
} from "hooks/audit-logs/useAuditLogs";
import auditLogsService from "services/auditLogsService";
import { getUsersWithCache, cacheUsers } from "pages/AuditLogs/UserCache";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { selectFilter, customFilter } from "react-bootstrap-table2-filter";
import { useTranslation } from "react-i18next";
import { Card, CardBody } from "reactstrap";
import DetailAuditLogs from "pages/AuditLogs/DetailAuditLogs";
import { auditLogActions, auditLogTypes } from "pages/AuditLogs/utils";

const CustomerAuditLogsTable = ({ customerCode }) => {
  const { t } = useTranslation(["customer", "auditLogs"]);
  const [columnsTable, setColumnsTable] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [detailModal, setDetailModal] = useState(false);
  const [dataDetail, setDataDetail] = useState({});
  const [mode, setMode] = useState("");
  const { data: logAdminUsers } = useLogAdminUsers();

  const logAdminUsersOptions = useMemo(() => {
    const options = [];
    if (logAdminUsers?.items) {
      logAdminUsers.items.forEach(user => {
        const displayName = user.display_name || user.username || user.email;
        options.push({
          label: displayName,
          value: user.code,
        });
      });
    }
    return options;
  }, [logAdminUsers]);

  const logAdminUsersEmailOptions = useMemo(() => {
    const options = [];
    if (logAdminUsers?.items) {
      logAdminUsers.items.forEach(user => {
        if (user.email) {
          options.push({
            label: user.email,
            value: user.code,
          });
        }
      });
    }
    return options;
  }, [logAdminUsers]);

  const [params, setParams] = useState({
    page: 1,
    sizePerPage: 10,
    sort: "-maker_date",
    columnFilter: {
      "filter[type][in]": ["customer_location", "customer_location_transport"],
    },
    keywords: "",
  });

  const {
    data: auditLogsData,
    isLoading,
    refetch,
  } = useCustomerAuditLogs({
    customerCode: customerCode,
    enabled: !!customerCode,
    params: params,
  });
  useEffect(() => {
    if (params && Object.keys(params).length > 0) {
      refetch();
    }
  }, [params, refetch]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!auditLogsData?.items || auditLogsData.items.length === 0) {
        setUsersMap({});
        return;
      }

      const uniqueUserCodes = [
        ...new Set(
          auditLogsData.items
            .map(item => item.user_code)
            .filter(code => code && code.trim() !== "")
        ),
      ];

      if (uniqueUserCodes.length === 0) {
        setUsersMap({});
        return;
      }

      const { cached, missing } = getUsersWithCache(uniqueUserCodes);

      setUsersMap(cached);

      if (missing.length === 0) {
        return;
      }

      setIsLoadingUsers(true);
      try {
        const userPromises = missing.map(userCode =>
          auditLogsService.listMsplogisticUsers({
            params: {
              page: 1,
              "per-page": 10,
              sort: "-maker_date",
              keywords: "",
              expand: "maker,checker",
              fields: "",
              columnFilter: {
                "filter[code]": userCode,
              },
            },
          })
        );

        const userResponses = await Promise.all(userPromises);
        const newUsers = {};

        userResponses.forEach((res, index) => {
          const userCode = missing[index];
          const userData = res?.data?.items?.[0] || res?.data?.[0] || null;
          if (userData) {
            newUsers[userCode] = userData;
          }
        });

        if (Object.keys(newUsers).length > 0) {
          cacheUsers(newUsers);
        }

        setUsersMap({ ...cached, ...newUsers });
      } catch (e) {
        setUsersMap(cached);
      } finally {
        setIsLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [auditLogsData]);

  const formatTimestamp = timestamp => {
    if (!timestamp) return "-";
    try {
      const timestampMs =
        typeof timestamp === "string"
          ? parseInt(timestamp) * 1000
          : timestamp * 1000;
      const date = new Date(timestampMs);
      return date.toLocaleString("vi-VN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch (e) {
      return timestamp;
    }
  };

  const selectOptionsTable = (name = "") => {
    const select = {};
    switch (name) {
      case "action":
        auditLogActions.forEach(item => {
          select[item.key] = item.label;
        });
        break;
      case "type":
        const customerTypes = auditLogTypes.filter(
          type =>
            type.key === "customer_location" ||
            type.key === "customer_location_transport"
        );
        customerTypes.forEach(item => {
          select[item.key] = item.label;
        });
        break;
      default:
        break;
    }
    return select;
  };

  useEffect(() => {
    const columns = [
      {
        key: "user_name",
        dataField: "user_code",
        text: t("audit_logs_table.user_name"),
        // sort: true,
        filter: customFilter({
          type: CUSTOM_SELECT_FILTER_TABLE_TYPE,
        }),
        filterRenderer: (onFilter, column) => (
          <CustomSelectFilter
            onFilter={onFilter}
            column={column}
            paramFilter={params.columnFilter}
            dataSelect={logAdminUsersOptions}
            placeholder={t("placeholder.select", {
              title: t("audit_logs_table.user_name"),
            })}
          />
        ),
        formatter: (cell, row) => {
          const userInfo = usersMap[row.user_code];
          return userInfo?.display_name || userInfo?.username || "-";
        },
      },
      {
        key: "user_email",
        dataField: "user_code",
        text: t("audit_logs_table.user_email"),
        // sort: true,
        filter: customFilter({
          type: CUSTOM_SELECT_FILTER_TABLE_TYPE,
        }),
        filterRenderer: (onFilter, column) => (
          <CustomSelectFilter
            onFilter={onFilter}
            column={column}
            paramFilter={params.columnFilter}
            dataSelect={logAdminUsersEmailOptions}
            placeholder={t("placeholder.select", {
              title: t("audit_logs_table.user_email"),
            })}
          />
        ),
        formatter: (cell, row) => {
          const userInfo = usersMap[row.user_code];
          return userInfo?.email || "-";
        },
      },
      {
        dataField: "action",
        text: t("audit_logs_table.action"),
        sort: true,
        align: "center",
        headerStyle: { width: "120px" },
        filter: selectFilter({
          placeholder: t("placeholder.select", {
            title: t("audit_logs_table.action"),
          }),
          options: selectOptionsTable("action"),
        }),
        formatter: (cell, row) => {
          const actionValue = row.action;
          const action = actionValue
            ? t(`auditLogs:action.${actionValue}`)
            : "-";
          return action;
        },
      },
      {
        dataField: "type",
        text: t("audit_logs_table.type"),
        sort: true,
        align: "center",
        headerStyle: { width: "140px" },
        filter: selectFilter({
          placeholder: t("placeholder.select", {
            title: t("audit_logs_table.type"),
          }),
          options: selectOptionsTable("type"),
        }),
        formatter: (cell, row) => {
          const typeValue = row.type || row.entity_type || "";
          if (!typeValue) return "-";

          let type = typeValue;
          if (typeValue === "customer_location")
            type = t("auditLogs:type.CUSTOMER_LOCATION");
          if (typeValue === "customer_location_transport")
            type = t("auditLogs:type.CUSTOMER_LOCATION_TRANSPORT");

          return type;
        },
      },
      {
        dataField: "maker_date",
        text: t("audit_logs_table.maker_date"),
        sort: true,
        align: "center",
        headerStyle: { width: "160px" },
        formatter: (cell, row) => formatTimestamp(row.maker_date),
        filter: customFilter({
          type: CUSTOM_DATE_FILTER_TABLE_TYPE,
        }),
        filterRenderer: (onFilter, column) => (
          <CustomDateFilter
            onFilter={onFilter}
            column={column}
            paramFilter={params.columnFilter}
            showOnlyOptions={["clear", "eq"]}
          />
        ),
      },
    ];

    setColumnsTable(columns);
  }, [t, usersMap, params.columnFilter, logAdminUsers]);

  const handleTableChange = useCallback(
    (type, { page, sizePerPage, sortField, sortOrder, columnFilter }) => {
      const column_filter = columnFilter || {};
      const cleanedFilters = {};
      let shouldResetPage = false;

      Object.keys(column_filter).forEach(key => {
        const value = column_filter[key];

        if (
          value !== null &&
          value !== undefined &&
          value !== "" &&
          value !== " "
        ) {
          if (typeof value === "object" && value !== null && "label" in value) {
            if (key === "user_code") {
              let user =
                logAdminUsers?.items?.find(u => u.code === value.value) ||
                logAdminUsers?.items?.find(u => u.email === value.value);

              if (user) {
                cleanedFilters["filter[user_code]"] = user.code;
                shouldResetPage = true;
              }
            }
          } else if (key === "action") {
            cleanedFilters[`action`] = value;
            shouldResetPage = true;
          } else if (key === "type") {
            cleanedFilters[`type`] = value;
            shouldResetPage = true;
          } else if (key === "filter[maker_date][gte]") {
            cleanedFilters[`filter[maker_date]`] = value;
            shouldResetPage = true;
          } else if (key === "filter[maker_date][eq]") {
            cleanedFilters[`filter[maker_date]`] = value;
            shouldResetPage = true;
          } else if (key === "filter[maker_date][lte]") {
            return;
          } else if (key !== "user_code") {
            cleanedFilters[key] = value;
            shouldResetPage = true;
          }
        } else {
          if (key === "user_code") {
            shouldResetPage = true;
          } else if (
            key === "action" ||
            key === "type" ||
            key === "filter[maker_date][gte]" ||
            key === "filter[maker_date][eq]"
          ) {
            shouldResetPage = true;
          }
        }
      });

      let apiSortField = sortField;
      if (sortField === "audit_action") {
        apiSortField = "action";
      }

      setParams(prev => ({
        ...prev,
        sort: apiSortField ? sortOrder + apiSortField : prev.sort,
        columnFilter: cleanedFilters,
        page: shouldResetPage ? 1 : page || 1,
        sizePerPage: sizePerPage || 10,
      }));
    },
    [logAdminUsers]
  );

  const onSearchAllHandle = value => {
    setParams(prev => ({
      ...prev,
      keywords: value,
      page: 1,
    }));
  };

  const toggleDetailModal = () => {
    setDetailModal(!detailModal);
  };

  const onClickView = row => {
    setDataDetail(row);
    setMode("view");
    toggleDetailModal();
  };

  const listRowAction = [
    {
      icon: <i className="fas fa-eye fa-lg text-info ms-2" />,
      text: t("audit_logs_table.actions.view"),
      onClick: onClickView,
    },
  ];

  return (
    <>
      <Card>
        <CardBody>
          <RemoteDataTable
            keyField="id"
            loading={isLoading || isLoadingUsers}
            data={auditLogsData?.items || []}
            columns={columnsTable}
            page={params.page}
            sizePerPage={params.sizePerPage}
            totalSize={auditLogsData?.totalCount || 0}
            onTableChange={handleTableChange}
            onSearchAllHandle={onSearchAllHandle}
            listRowAction={listRowAction}
            isShowRowIndex={true}
            isShowSearchAll={false}
            isShowPagination={auditLogsData?.totalCount > params.sizePerPage}
            isCustomerAuditLogsTable={true}
          />
        </CardBody>
      </Card>

      <DetailAuditLogs
        dataDetail={dataDetail}
        isOpenModal={detailModal}
        toggleModal={toggleDetailModal}
        usersMap={usersMap}
      />
    </>
  );
};

export default CustomerAuditLogsTable;
